/** * Usage: This component provides a calendar picker for individually selecting multiple dates. *
It is NOT for selecting a range of dates (e.g. selecting a start and end date in a calendar). * If
you need that behaviour, this component will need to be modified. * * By default, when a Monday is
selected, this component will automatically select all the other days within that week. * In future,
changes could be made to make this behaviour optional. */

<template>
  <div class="date-input date-input-exclude">
    <v-menu
      ref="menu"
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      min-width="290px"
    >
      <template #activator="{ props }">
        <v-combobox
          v-bind="props"
          :model-value="formattedDates"
          multiple
          :placeholder="$t('workpackagePage.scope.datesLabel')"
          @update:model-value="expandMondaysIntoFullWeeks"
        >
          <template #append-inner>
            <v-icon size="small" v-bind="props" @click="menu = true">
              mdi-calendar-blank-outline
            </v-icon>
          </template>
        </v-combobox>
      </template>
      <!-- eslint-disable vuetify/no-deprecated-props -->
      <!-- This was fixed here, but not on lint https://github.com/vuetifyjs/vuetify/pull/20096 -->
      <v-date-picker
        :allowed-dates="allowedDates"
        :first-day-of-week="1"
        :model-value="value.map(getTzZeroedDate)"
        multiple
        hide-header
        scrollable
        @update:model-value="expandMondaysIntoFullWeeks"
      >
        <template #actions>
          <v-spacer />
          <v-btn variant="text" color="primary" @click="clearSelectedDates">
            {{ $t('actions.unselect') }}
          </v-btn>
          <v-btn variant="text" color="primary" @click="menu = false">
            {{ $t('actions.cancel') }}
          </v-btn>
          <v-btn variant="text" color="primary" @click="updateSelectedDates">
            {{ $t('actions.ok') }}
          </v-btn>
        </template>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import { map, range, flatMap } from 'lodash';

// Stores
import { useContextStore } from '@/stores/context';

// Composables
import { useDayjs } from '@/composables/use-dayjs';

export default {
  props: {
    allowedDates: {
      type: Function,
      required: false,
      default: () => true,
    },
    value: {
      type: Array,
      required: true,
    },
  },

  setup() {
    const { dayjs, formatUtcDate, getTzZeroedDate } = useDayjs();

    return { dayjs, formatUtcDate, getTzZeroedDate };
  },

  data() {
    return { menu: false };
  },

  computed: {
    ...mapState(useContextStore, ['getDateFormats', 'getDefaultReverseFormat']),

    formattedDates() {
      return (this.value || [])
        .filter(date => this.dayjs(date).isoWeekday() === 1) // only show Mondays
        .map(date => this.dayjs(date).format(this.getDateFormats.long));
    },
  },

  methods: {
    // hack to select weeks. should be removed if feature request is accepted. https://github.com/vuetifyjs/vuetify/issues/4566
    expandMondaysIntoFullWeeks(allPreviouslySelectedWeekdays) {
      const daysInWeek = 7;
      const monday = 1;
      const mondays = allPreviouslySelectedWeekdays.filter(
        date => this.dayjs(date).day() === monday
      );
      const expandMondayIntoFullWeek = someMonday =>
        map(range(daysInWeek), i => this.getTzZeroedDate(this.dayjs(someMonday).add(i, 'day')));

      const exclusions = flatMap(mondays, expandMondayIntoFullWeek);

      this.$emit('input', exclusions);
    },
    updateSelectedDates() {
      this.menu = false;
    },
    clearSelectedDates() {
      this.$emit('input', []);
      this.updateSelectedDates();
    },
  },
};
</script>

<style lang="scss" scoped>
.v-select.v-select--chips .v-select__selections {
  min-height: auto !important;
}

.date-input.date-input-exclude {
  width: 100%;
  :deep(.v-input),
  :deep(.v-select) {
    max-width: 223px;
    margin: 0;
    padding: 0;
  }
  :deep(.v-input__slot) {
    background: white;
    border-bottom: blue 1px solid !important;
    width: 100% !important;
    height: auto !important;

    :deep(.v-select__slot) {
      width: 100% !important;
      height: auto !important;
    }
  }
  :deep(.v-select__selections) {
    padding-left: 3px;
    max-height: 100px;
    overflow-y: scroll;

    :deep(input) {
      max-height: 50px;
      text-overflow: ellipsis;
      padding-top: 3px;
      padding-bottom: 3px;
    }
  }
  :deep(.v-select__selection--comma) {
    margin-bottom: 3px;
    margin-top: 3px;
    padding-left: 0px;
  }
}
</style>
